import { uploadToCloudinary } from '@utils/cloudinary';
import getQualifiedImageUrl from '@utils/imageUrlProcessor';
import { authorizedServerFetch } from 'actions/server.actions';
import { isBefore } from 'date-fns';
import produce from 'immer';
import { get, isArray, isEmpty, size, sortBy, toLower, trim } from 'lodash-es';
import {
  saveCampaign,
  saveProject,
  saveVolunteeringOpportunity,
} from './postActions';
import useStore from './store';

const computeExtraFieldsForCampaign = (item) => ({
  ...item,
  status: isBefore(new Date(), new Date(item.sourcePostData.endDate))
    ? 'Active'
    : 'Completed',
});

const computeExtraFieldsForVolunteering = (volunteering) => {
  const { startsOn, endsOn, applyDeadline } = volunteering;
  const data = { ...volunteering };

  if (isBefore(new Date(), new Date(applyDeadline))) {
    data.status = 'Recruiting';
  } else if (isBefore(new Date(), new Date(startsOn))) {
    data.status = 'Upcoming';
  } else if (isBefore(new Date(), new Date(endsOn))) {
    data.status = 'In Progress';
  } else {
    data.status = 'Completed';
  }

  if (data.volunteers) {
    data.numActiveVolunteers = data.volunteers.filter(
      (item) => item.status === 'active',
    ).length;
    data.numPendingApplications = data.volunteers.filter(
      (item) => item.status === 'pending',
    ).length;
  } else {
    data.numActiveVolunteers = 0;
    data.numPendingApplications = 0;
  }

  return data;
};

const confirmInKindDonation = async (donationId) => {
  const { data, ok } = await authorizedServerFetch(
    `npo-admin/donation/${donationId}/approve`,
    {
      method: 'POST',
      body: {},
    },
  );
  return { data: data?.data, errored: !ok };
};

const processVolunteerApplication = async (applicationId, status) => {
  const formData = new FormData();
  formData.append('data', JSON.stringify({ status: status }));

  const { data, ok } = await authorizedServerFetch(
    `npo-admin/volunteer-application/${applicationId}`,
    {
      method: 'POST',
      body: formData,
    },
  );
  return { data: data?.data, errored: !ok };
};

const processUserOnboardApplication = async (applicationId, status) => {
  const formData = new FormData();
  formData.append('data', JSON.stringify({ status: status }));

  const { data, ok } = await authorizedServerFetch(
    `npo-admin/user-onboard-application/${applicationId}`,
    {
      method: 'POST',
      body: formData,
    },
  );
  return { data: data?.data, errored: !ok };
};

const fetchVolunteeringForNonProfit = async () => {
  const { data, ok } = await authorizedServerFetch(
    'npo-admin/volunteer-opportunities',
  );
  const responseData = data?.data;
  if (ok) {
    const processed = responseData.volunteeringOpportunities.map((item) =>
      computeExtraFieldsForVolunteering(item),
    );
    useStore.setState(
      produce((draft) => {
        draft.nonProfitOrg.volunteerOpportunities = processed;
        draft.nonProfitOrg.skills = sortBy(
          sortBy(responseData.skills, 'skill_title'),
        );
        draft.nonProfitOrg.causes = sortBy(
          sortBy(responseData.causes, 'cause_title'),
        );
        draft.nonProfitOrg.volunteerSearchText = '';
      }),
    );
  }
};

const saveVolunteeringOpportunityAsAdmin = async (
  formValues,
  volunteeringPost,
  images,
  videos,
) => {
  const isNew = isEmpty(volunteeringPost);
  const { data, ok } = await saveVolunteeringOpportunity(
    formValues,
    volunteeringPost,
    images,
    videos,
  );

  if (ok) {
    const { post } = data;
    useStore.setState(
      produce((draft) => {
        const updated =
          post.sourcePostData.volunteeringOpportunity ||
          post.sourcePostData.volunteerOpportunity;
        if (isNew) {
          draft.nonProfitOrg.volunteerOpportunities.push(
            computeExtraFieldsForVolunteering(updated),
          );
        } else {
          const index = draft.nonProfitOrg.volunteerOpportunities.findIndex(
            (item) => item.id === updated.id,
          );
          draft.nonProfitOrg.volunteerOpportunities[index] =
            computeExtraFieldsForVolunteering(updated);
        }
      }),
    );
  }
};

const fetchVolunteers = async (id) => {
  const { data } = await authorizedServerFetch(
    `npo-admin/volunteer-opportunities/${id}/volunteers`,
  );
  return data?.data?.volunteers || [];
};

const fetchEffortsLogged = async (id) => {
  const { data } = await authorizedServerFetch(
    `npo-admin/volunteer-opportunities/${id}/efforts`,
  );

  return data?.data?.efforts || [];
};

const filterVolunteeringForNonProfit = () => {
  const allVolunteering =
    useStore.getState().nonProfitOrg.volunteerOpportunities;
  const searchText = trim(
    toLower(useStore.getState().nonProfitOrg.volunteerSearchText),
  );

  if (searchText.length === 0) {
    return allVolunteering;
  }

  return allVolunteering.filter((item) =>
    item.name.toLowerCase().includes(searchText.toLowerCase()),
  );
};

const fetchCampaignsForNonProfit = async () => {
  const { data, ok } = await authorizedServerFetch('npo-admin/campaigns');
  if (ok) {
    useStore.setState(
      produce((draft) => {
        const campaigns = data?.data?.posts.map((item) =>
          computeExtraFieldsForCampaign(item),
        );
        draft.nonProfitOrg.campaigns = campaigns;
        draft.nonProfitOrg.campaignsSearchText = '';
      }),
    );
  }
};

const getCampaignsByType = (campaignType) => {
  const allCampaigns = useStore.getState().nonProfitOrg.campaigns;
  const searchText = trim(
    toLower(useStore.getState().nonProfitOrg.campaignsSearchText),
  );

  const campaigns = [];
  allCampaigns.forEach((item) => {
    let show = true;
    if (searchText.length > 0) {
      show = item.sourcePostData.title
        .toLowerCase()
        .includes(searchText.toLowerCase());
    }
    if (show) {
      if (item.sourcePostData.campaignType === campaignType) {
        campaigns.push(item);
      }
    }
  });
  return campaigns;
};

const useGetCampaignById = (id) => {
  const campaigns = useStore((state) => state.nonProfitOrg.campaigns);
  if (!id) return null;
  return campaigns.find((item) => item.id === id);
};

const saveCampaignAsAdmin = async (formValues, post) => {
  const isNew = isEmpty(post);
  const { data, errored } = await saveCampaign(formValues, null, null, post);
  if (!errored) {
    useStore.setState(
      produce((draft) => {
        if (isNew) {
          draft.nonProfitOrg.campaigns.push(
            computeExtraFieldsForCampaign(data.post),
          );
        } else {
          const index = draft.nonProfitOrg.campaigns.findIndex(
            (item) => item.id === data.post.id,
          );
          draft.nonProfitOrg.campaigns[index] = computeExtraFieldsForCampaign(
            data.post,
          );
        }
      }),
    );
  }
};

const fetchProjectsForNonProfit = async () => {
  const { data, ok } = await authorizedServerFetch('nonprofit-admin/projects');
  if (ok) {
    useStore.setState(
      produce((draft) => {
        draft.nonProfitOrg.projects = data?.data || [];
        draft.nonProfitOrg.projectsSearchText = '';
      }),
    );
  }
};

const getProjectsByType = (campaignType) => {
  const allProjects = useStore.getState().nonProfitOrg.projects;
  const searchText = trim(
    toLower(useStore.getState().nonProfitOrg.projectsSearchText),
  );

  const projects = [];
  allProjects.forEach((item) => {
    let show = true;
    if (searchText.length > 0) {
      show = item.sourcePostData.name
        .toLowerCase()
        .includes(searchText.toLowerCase());
    }
    if (show) {
      if (item.sourcePostData.campaignType === campaignType) {
        projects.push(item);
      }
    }
  });
  return projects;
};

const useGetProjectById = (id) => {
  const projects = useStore((state) => state.nonProfitOrg.projects);
  if (!id) return null;
  const project = projects.find((item) => item.sourcePostData.id === id);
  return project;
};

const saveProjectAsAdmin = async (formValues, project, images, videos) => {
  const isNew = isEmpty(project);
  const { data, ok } = await saveProject(formValues, project, images, videos);

  console.log('saveProjectAsAdmin data', data);

  if (ok) {
    const { post } = data;

    useStore.setState(
      produce((draft) => {
        if (isNew) {
          draft.nonProfitOrg.projects.unshift(post);
        } else {
          const index = draft.nonProfitOrg.projects.findIndex(
            (item) => item.id === post.id,
          );
          // draft.nonProfitOrg.projects.splice(index, 1);
          // draft.nonProfitOrg.projects.unshift(post);
          draft.nonProfitOrg.projects[index] = post;
        }
      }),
    );
  }
};

const saveOverviewAsNpAdmin = async (formData, nonProfitId) => {
  const { data, ok } = await authorizedServerFetch(
    'npo-admin/organization/profile',
    {
      method: 'POST',
      body: formData,
    },
  );

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const localCopy = draft.nonProfit.cached[nonProfitId];
        draft.nonProfit.cached[nonProfitId] = localCopy
          ? { ...localCopy, ...data?.data }
          : data?.data;
        draft.user.profile.company.name = data?.data?.name;
        draft.user.profile.company.logoUrl = getQualifiedImageUrl(
          data?.data?.logo_url,
        );
        draft.user.profile.nonProfitCompany = data?.data;
      }),
    );
  }
};

const updateNonProfitProfile = (data, nonProfitId) => {
  // console.log('updateNonProfitProfile', data, nonProfitId);
  if (data?.data) {
    useStore.setState(
      produce((draft) => {
        const localCopy = draft.nonProfit.cached[nonProfitId];
        draft.nonProfit.cached[nonProfitId] = localCopy
          ? { ...localCopy, ...data?.data }
          : data?.data;
        draft.user.profile.company.name = data?.data?.name;
        draft.user.profile.company.logoUrl = getQualifiedImageUrl(
          data?.data?.logo_url,
        );
        draft.user.profile.nonProfitCompany = data?.data;
      }),
    );
  }
};

const issueVolunteerCertificate = async (
  volunteerId,
  volunteerOpportunityId,
) => {
  const { data, ok } = await authorizedServerFetch(
    `npo-admin/volunteer-opportunities/${volunteerOpportunityId}/volunteers/${volunteerId}/certificate`,
    {
      method: 'POST',
      body: {},
    },
  );
  return { errored: !ok, data: data?.data };
};

const fetchNonProfitDonations = async () => {
  const { data, ok } = await authorizedServerFetch(`npo-admin/donations`);
  console.log('fetchNonProfitDonations', data);
  if (ok) {
    useStore.setState(
      produce((draft) => {
        draft.nonProfitOrg.donations = data?.data;
      }),
    );
  }
};

const filterDonationsForNonProfit = () => {
  const allDonations = useStore.getState().nonProfitOrg.donations;
  const searchText = trim(
    toLower(useStore.getState().nonProfitOrg.donationsSearchText),
  );

  if (searchText.trim().length === 0) {
    return allDonations;
  }

  const searchFields = ['first_name', 'last_name', 'email', 'orgDetails.name'];
  return allDonations.filter((d) =>
    searchFields.some((field) => {
      const fieldValue = get(d.user, field);
      return fieldValue && fieldValue.toLowerCase().includes(searchText);
    }),
  );
};

export {
  confirmInKindDonation,
  fetchCampaignsForNonProfit,
  fetchEffortsLogged,
  fetchNonProfitDonations,
  fetchProjectsForNonProfit,
  fetchVolunteeringForNonProfit,
  fetchVolunteers,
  filterDonationsForNonProfit,
  filterVolunteeringForNonProfit,
  getCampaignsByType,
  getProjectsByType,
  issueVolunteerCertificate,
  processUserOnboardApplication,
  processVolunteerApplication,
  saveCampaignAsAdmin,
  saveOverviewAsNpAdmin,
  saveProjectAsAdmin,
  saveVolunteeringOpportunityAsAdmin,
  updateNonProfitProfile,
  useGetCampaignById,
  useGetProjectById,
};
